import type { Ref } from 'react';
import { NewPassword, PASSWORD_MAX_LENGTH } from '@grc/shared';
import { t, type Code } from '../i18n';
import { Field } from './Field';

/** What a password form holds: the current password (when it's asked), the new one, and again. */
export type PasswordValues = { current: string; next: string; repeat: string };

export const NO_PASSWORDS: PasswordValues = { current: '', next: '', repeat: '' };

/**
 * What the phone finds before anything is sent (D22): an empty field (it takes the cursor), or the
 * line to say. The server checks the rest: the current password, and a new one that's the same.
 */
export type PasswordCheck =
  { kind: 'ok' } | { kind: 'empty'; field: keyof PasswordValues } | { kind: 'error'; code: Code };

/**
 * The checks of both password forms, the forced change and Profile's (A3, A6): the fields in their
 * order, an empty one first, the new password's rule (NewPassword) before its repeat is asked for,
 * then the two new ones alike.
 */
export function checkPasswords(values: PasswordValues, askCurrent: boolean): PasswordCheck {
  if (askCurrent && values.current === '') return { kind: 'empty', field: 'current' };
  if (values.next === '') return { kind: 'empty', field: 'next' };
  if (!NewPassword.safeParse(values.next).success) {
    return { kind: 'error', code: 'error.passwordRule' };
  }
  if (values.repeat === '') return { kind: 'empty', field: 'repeat' };
  if (values.next !== values.repeat) return { kind: 'error', code: 'error.mismatch' };
  return { kind: 'ok' };
}

type PasswordFieldsProps = {
  /** Whose password it is, for the phone's password manager, which saves the new one under it. */
  username: string;
  /** Whether the current password is asked: always, but straight after a one-time login (D22). */
  askCurrent: boolean;
  values: PasswordValues;
  onChange: (values: PasswordValues) => void;
  currentRef: Ref<HTMLInputElement>;
  nextRef: Ref<HTMLInputElement>;
  repeatRef: Ref<HTMLInputElement>;
};

/**
 * The fields of a change of password (D21, D22): the current password when it's asked, the new one
 * and the new one again, in the forced change's words. The forced change and Profile's change of
 * password both use them, each with its own buttons.
 */
export function PasswordFields({
  username,
  askCurrent,
  values,
  onChange,
  currentRef,
  nextRef,
  repeatRef,
}: PasswordFieldsProps) {
  return (
    <>
      {/* For the phone's password manager: whose new password this is. */}
      <input
        className="visually-hidden"
        type="text"
        name="username"
        autoComplete="username"
        value={username}
        readOnly
        tabIndex={-1}
        aria-hidden="true"
      />
      {askCurrent && (
        <Field
          ref={currentRef}
          label={t('password.current')}
          name="current-password"
          type="password"
          autoComplete="current-password"
          maxLength={PASSWORD_MAX_LENGTH}
          aria-required="true"
          value={values.current}
          onChange={(e) => onChange({ ...values, current: e.target.value })}
        />
      )}
      <Field
        ref={nextRef}
        label={t('password.new')}
        hint={t('password.newHint')}
        name="new-password"
        type="password"
        autoComplete="new-password"
        maxLength={PASSWORD_MAX_LENGTH}
        aria-required="true"
        value={values.next}
        onChange={(e) => onChange({ ...values, next: e.target.value })}
      />
      <Field
        ref={repeatRef}
        label={t('password.repeat')}
        name="repeat-password"
        type="password"
        autoComplete="new-password"
        maxLength={PASSWORD_MAX_LENGTH}
        aria-required="true"
        value={values.repeat}
        onChange={(e) => onChange({ ...values, repeat: e.target.value })}
      />
    </>
  );
}
